import React, { useState } from 'react';
import { MentorNetworkMentorship } from 'apis/MentorAPI/types';
import Button from 'ui/elements/buttons/Button';
import TrashIcon from 'ui/elements/icons/TrashIcon';
import SectionHeading from 'ui/elements/SectionHeading';
import UserElement from 'ui/domain/Users/UserElement';
import Card from 'ui/views/cards/Card';
import MentorAssignmentDialog from './MentorAssignmentDialog';
import ContactInfoBlock from 'ui/domain/Contact/ContactDetails';
import useDialogHandler from 'hooks/useDialogHandler';
import UserProfileDialog from 'domain/users/UserProfile/UserProfileDialog';
import { TinyCommunity } from 'types/company/community';
import { communitiesApi } from 'apis/CompanyAPI/communities/communitiesApi';
import useLazyResource from 'util/resource/useLazyResource';
import { useNotifyContext } from 'contexts/NotificationContext';

interface Props {
  mentorship: MentorNetworkMentorship;
  companyId: number;
  companyName: string;
  community: TinyCommunity;
  refresh: () => void;
}

export default function MentorAssignment(props: Props) {
  const notify = useNotifyContext();
  const { mentor } = props.mentorship;

  const assignMentorDialogHandler = useDialogHandler(false);
  const [userDialogId, setUserDialogId] = useState<UUID | undefined>(undefined);

  const [unassignMentor, isUnassigning] = useLazyResource(
    (_: void) =>
      communitiesApi.mentorNetwork.mentorships.mentorAssignments.remove(
        props.community.slug,
        props.companyId,
        props.mentorship.id,
      ),
    {
      onSuccess: () => {
        notify('success', 'Mentor unassigned');
        props.refresh();
      },
      onFailure: () => notify('error', 'Failed to unassign mentor'),
    },
  );

  return (
    <>
      <SectionHeading heading="Mentor" addMobileMargin />
      <Card>
        {mentor ? (
          <>
            <div className="u-flex u-flex-align-center u-content-spacing-top u-flex-space-between">
              <button onClick={() => setUserDialogId(mentor.cwUserId)}>
                <UserElement user={mentor} />
              </button>
              <Button kind="tertiary" isLoading={isUnassigning} onClick={() => unassignMentor(undefined)}>
                <TrashIcon color="red" />
              </Button>
            </div>
            <ContactInfoBlock email={mentor.email} phone={mentor.phone} />
          </>
        ) : (
          <div>
            <div>No mentor assigned.</div>
            <Button
              onClick={assignMentorDialogHandler.open}
              className="u-half-spacing-top u-half-spacing-right"
              kind="secondary"
            >
              Assign mentor
            </Button>
          </div>
        )}
        {assignMentorDialogHandler.isOpen && (
          <MentorAssignmentDialog
            onClose={assignMentorDialogHandler.close}
            community={props.community}
            companyId={props.companyId}
            companyName={props.companyName}
            mentorship={props.mentorship}
            onAssign={props.refresh}
          />
        )}
        {userDialogId && <UserProfileDialog cwUserId={userDialogId} onClose={() => setUserDialogId(undefined)} />}
      </Card>
    </>
  );
}
